import React from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { Helmet } from 'react-helmet';
import { RouteComponentProps, Link, navigate } from '@reach/router';
import { appDispatch, RootState } from 'store/store';
import messages from './Messages';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Collapsible from 'components/Collapsible/Collapsible';
import { SwitchChoiceType } from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/MultiChoiceQuestion/MultiChoiceFields/styles';
import {
  ConfigItems,
  ProjectItemContainer,
} from 'components/MakhsanConfigPanel/styles';
import ProjectTemplates from './ProjectTemplates';
import ProjectMembers from './ProjectMembers';
import ProjectEditor from './ProjectEditor';
import { orange } from 'utils/style/colors';
import { isCompanyOwnerOrSupport } from 'store/companies/companiesUtils';
import {
  ICompany,
  IProjectMemberView,
  IProjectStore,
  TProjects,
} from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';

import { addGuideToList } from 'store/siteGuide/SiteGuideSlice';
import { BatchAddMemberToProjects } from './BatchAddMemberToProjects';
import {
  addProjectMember,
  addProjectMemberFn,
  removeProjectMember,
  removeProjectMemberFn,
  updateProjectMember,
  updateProjectMemberFn,
} from '../../../store/companies/companiesSlice';
import i18n from '../../../i18n';

interface IStateToProps {
  company: ICompany;
  isCompanyOwnerOrSupport: boolean;
  isCompanyAdmin: boolean;
  projects: TProjects;
  thisUser: IThisUserState;
}
interface IDispatchToProps {
  addGuideToList: (key: string | string[]) => Promise<any>;
  addMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof addProjectMemberFn>;
  removeMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof removeProjectMemberFn>;
  updateMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof updateProjectMemberFn>;
}

interface IOwnProps {
  companyId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  RouteComponentProps &
  IDispatchToProps;
interface IState {
  showArchived: boolean;
  selectedProject?: number;
  selectedView?: 'members' | 'templates' | 'editor';
}
class ProjectsList extends React.PureComponent<IComponentProps, IState> {
  handleClickCreateButton = () => {
    const companyId = Number(this.props.companyId);
    navigate(`/t/${companyId}/admin/project/create`);
  };
  state = {
    showArchived: false,
    selectedProject: undefined as number | undefined,
    selectedView: undefined,
  };
  componentDidMount() {
    this.props.addGuideToList([
      i18n.t('managingUsersCommittee', { ns: 'companyManagement' }),
    ]);
  }

  render() {
    const { projects, isCompanyOwnerOrSupport, company, isCompanyAdmin } =
      this.props;
    const { showArchived } = this.state;
    const allTags = Object.values(projects).reduce((current, item) => {
      if (!showArchived && item.archived) return current!;
      item.tags?.forEach((it: string) => {
        current!.add(it);
      });
      if (!item.tags?.length)
        current!.add(i18n.t('other', { ns: 'companyManagement' }));
      return current!;
    }, new Set<string>());
    return (
      <React.Fragment>
        <Helmet>
          <title>{messages.projects(company.name)}</title>
          <meta name="description" content={messages.projects(company.name)} />
        </Helmet>
        <>
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {isCompanyAdmin && (
                <Button color="green" onClick={this.handleClickCreateButton}>
                  {
                    i18n.t('createNewProject', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </Button>
              )}
              {isCompanyOwnerOrSupport && (
                <BatchAddMemberToProjects
                  company={company}
                  updateMember={this.props.updateMember}
                  addMember={this.props.addMember}
                  removeMember={this.props.removeMember}
                />
              )}
              <SwitchChoiceType className="switch">
                <label>
                  {
                    i18n.t('showArchived', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => {
                      this.setState({ showArchived: e.target.checked });
                    }}
                  />
                  <span className="lever" />
                </label>
              </SwitchChoiceType>
            </div>
          </>
          <div style={{ display: 'flex', height: 'calc(100% - 50px)' }}>
            <ConfigItems
              width={this.state.selectedProject ? '300px' : undefined}
            >
              {Array.from(allTags).map((tag) => (
                <Collapsible
                  key={tag}
                  header={
                    <div>
                      <span>{tag}</span>
                    </div>
                  }
                  customBodyStyle={{ padding: 0 }}
                >
                  <div className="row">
                    <div className="section">
                      {Object.values(projects)
                        .filter(
                          (p) =>
                            (p.tags?.find((it) => it === tag) ||
                              (!p.tags?.length &&
                                tag ===
                                  i18n.t('other', {
                                    ns: 'companyManagement',
                                  }))) &&
                            (showArchived || !p.archived)
                        )
                        .sort((a, b) => {
                          if (b.name < a.name) {
                            return 1;
                          }
                          return 0;
                        })
                        .map((project) =>
                          this.projectItemView(project, isCompanyAdmin)
                        )}
                    </div>
                  </div>
                </Collapsible>
              ))}
            </ConfigItems>
            {this.state.selectedProject && (
              <ConfigItems>
                {this.state.selectedProject &&
                  this.state.selectedView === 'templates' && (
                    <ProjectTemplates
                      projectId={this.state.selectedProject.toString()}
                      companyId={this.props.companyId}
                    />
                  )}
                {this.state.selectedProject &&
                  this.state.selectedView === 'members' && (
                    <ProjectMembers
                      projectId={this.state.selectedProject.toString()}
                      companyId={this.props.companyId}
                    />
                  )}
                {this.state.selectedProject &&
                  this.state.selectedView === 'editor' && (
                    <ProjectEditor
                      projectId={this.state.selectedProject.toString()}
                      companyId={this.props.companyId}
                    />
                  )}
              </ConfigItems>
            )}
          </div>
        </>
      </React.Fragment>
    );
  }

  private projectItemView(project: IProjectStore, isCompanyAdmin: boolean) {
    return (
      <div key={project.id} className="col s12 m12 l12">
        <ProjectItemContainer
          className="card"
          isSelected={project.id === this.state.selectedProject}
        >
          <div className="card-content">
            <span className="">
              {project.name}
              {project.archived &&
                (('(' +
                  i18n.t('archived', {
                    ns: 'companyManagement',
                  })) as string) + ')'}
            </span>
          </div>
          <div className="card-action" style={{ display: 'flex' }}>
            {(isCompanyAdmin || project.myMembership?.admin) && (
              <Link
                to=""
                onClick={() => {
                  this.setState({
                    selectedProject: project.id,
                    selectedView: 'members',
                  });
                }}
              >
                {
                  i18n.t('members', {
                    ns: 'companyManagement',
                  }) as string
                }
              </Link>
            )}
            {isCompanyAdmin && (
              <React.Fragment>
                {
                  <Link
                    to=""
                    onClick={() => {
                      this.setState({
                        selectedProject: project.id,
                        selectedView: 'templates',
                      });
                    }}
                  >
                    {
                      i18n.t('templates', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </Link>
                }
                <span style={{ flexGrow: 1 }} />
                <Link
                  to=""
                  onClick={() => {
                    this.setState({
                      selectedProject: project.id,
                      selectedView: 'editor',
                    });
                  }}
                >
                  <Icon type={'edit'} color={orange} />
                </Link>
              </React.Fragment>
            )}
          </div>
        </ProjectItemContainer>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const companyId = Number(props.companyId);
  const thisUser = state.thisUser;
  const company = state.companies.data[companyId];
  const projects = Object.values(company.projects).filter(
    (project) => project.companyId === companyId
  );
  const isCompanyOwnerOrSupportMember = isCompanyOwnerOrSupport(
    thisUser,
    company
  );
  const isCompanyAdmin = company.members[Number(thisUser.userId)].admin;

  return {
    thisUser,
    projects,
    company,
    isCompanyOwnerOrSupport: isCompanyOwnerOrSupportMember,
    isCompanyAdmin,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  addGuideToList: (key) => appDispatch(addGuideToList(key)),

  addMember: (projectMember: IProjectMemberView, companyId: number) =>
    appDispatch(addProjectMember({ projectMember, companyId })),
  removeMember: (projectMember: IProjectMemberView, companyId: number) =>
    appDispatch(removeProjectMember({ projectMember, companyId })),
  updateMember: (projectMember: IProjectMemberView, companyId: number) =>
    appDispatch(updateProjectMember({ projectMember, companyId })),
});
export default connect(mapStateToProps, mapDispatchToProps)(ProjectsList);
